/**
 * Forces the client onto the newest deployed build, then hard-navigates.
 *
 * Signing a user out only clears auth state — the page keeps running whatever
 * JS bundle the currently-registered service worker handed it. Admin-triggered
 * sign-outs are a natural moment to also pick up a new release, so we ask every
 * registration to re-check for an update first. The worker in src/sw.ts calls
 * skipWaiting on install, so a newly-found build takes control on its own; we
 * just need to give it a beat and then navigate rather than soft-route.
 */
const UPDATE_TIMEOUT_MS = 3000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | void> {
  return Promise.race([
    promise,
    new Promise<void>((resolve) => window.setTimeout(resolve, ms)),
  ]);
}

export async function refreshServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await withTimeout(
      Promise.allSettled(registrations.map((r) => r.update())),
      UPDATE_TIMEOUT_MS,
    );
  } catch {
    // An update check is best effort — never block the sign-out on it.
  }
}

/**
 * Checks for a new build, then leaves the SPA with a real navigation so the
 * fresh worker serves the next document instead of the in-memory bundle.
 */
export async function refreshAndHardReload(target = "/auth"): Promise<void> {
  await refreshServiceWorker();
  window.location.replace(target);
}
